import React from 'react';
import Button from '@mui/material/Button';
import { Box, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import Backdrop from '@mui/material/Backdrop';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import CloseIcon from '@mui/icons-material/Close';

function ModuleCurrentHoursAdjustmentButton({
    course,
    currentMinutes,
    colors,
    handlePopOverClose,
    enqueueSnackbar,
    socketConnected,
    socket,
}) {
    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 800,
        bgcolor: colors.primary[600],
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
    };

    // for modal
    const [open, setOpen] = React.useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => { setOpen(false); handlePopOverClose(); };

    // Current total from props (read-only)
    const currentTotal = course.current_minutes_adjustment || 0;

    // Delta inputs
    const [sign, setSign] = React.useState("+");
    const [deltaHours, setDeltaHours] = React.useState(0);
    const [deltaMinutes, setDeltaMinutes] = React.useState(0);

    // Reset delta fields when course changes or modal opens
    React.useEffect(() => {
        setSign("+");
        setDeltaHours(0);
        setDeltaMinutes(0);
    }, [course]);

    const deltaTotal = (sign === "+" ? 1 : -1) * (deltaHours * 60 + deltaMinutes);
    const newTotal = currentTotal + deltaTotal;
    const isDeltaZero = deltaHours === 0 && deltaMinutes === 0;

    const handleSave = async () => {
        if (!socketConnected) {
            enqueueSnackbar("Socket is not connected. Please refresh the page.", { variant: "error" });
            return;
        }

        if (isDeltaZero) {
            enqueueSnackbar("Please enter a non-zero adjustment.", { variant: "warning" });
            return;
        }

        try {
            socket.emit("put.CurrentMinutesAdjustment", {
                course_id: course.id,
                current_minutes_adjustment: newTotal,
            });
        } catch (error) {
            console.error(error);
            enqueueSnackbar("Error: Cannot save Current Hours Adjustment at the moment.", { variant: "error" });
            return;
        }

        // Reset delta fields after save
        setSign("+");
        setDeltaHours(0);
        setDeltaMinutes(0);
        handleClose();
    };

    // Helper to format minutes for display (with sign)
    const formatAdjustment = (minutes) => {
        if (minutes === 0) return "0h 0m";
        const s = minutes < 0 ? "-" : "+";
        const absMinutes = Math.abs(minutes);
        const hours = Math.floor(absMinutes / 60);
        const mins = absMinutes % 60;
        if (hours === 0) return `${s}${mins}m`;
        if (mins === 0) return `${s}${hours}h`;
        return `${s}${hours}h ${mins}m`;
    };

    // Helper to format minutes without sign
    const formatHours = (minutes) => {
        if (minutes === undefined || minutes === null || minutes < 0) return "n/a";
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        if (hours === 0 && mins === 0) return "0h";
        if (hours === 0) return `${mins}m`;
        if (mins === 0) return `${hours}h`;
        return `${hours}h ${mins}m`;
    };

    // Module's current hours (base + adjustment)
    const moduleCurrentHours = currentMinutes + currentTotal;
    const moduleNewHours = currentMinutes + newTotal;

    return (
        <>
            <Button
                onClick={handleOpen}
                sx={{
                    p: 1,
                    width: "100%",
                    bgcolor: colors.grey[700],
                    borderRadius: "0px",
                    '&:hover': {
                        bgcolor: colors.grey[600],
                    },
                }}
            >
                Current hours adjustment
            </Button>
            <Modal
                aria-labelledby="current-hours-adjustment-modal"
                aria-describedby="current-hours-adjustment-modal-description"
                open={open}
                onClose={handleClose}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{
                    backdrop: {
                        timeout: 500,
                    },
                }}
            >
                <Fade in={open}>
                    <Box sx={style}>
                        <Typography variant="h4" color={colors.primary[450]}>
                            Current Hours Adjustment
                        </Typography>
                        <CloseIcon sx={{ position: "absolute", right: 10, top: 10, cursor: "pointer" }} onClick={handleClose} />

                        {/* Current info (read-only) */}
                        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                            <Box sx={{
                                flex: 1,
                                p: 2,
                                bgcolor: colors.primary[600],
                                borderRadius: 1,
                                textAlign: "center",
                            }}>
                                <Typography variant="body1" color={colors.primary[450]}>
                                    Module current hours
                                </Typography>
                                <Typography variant="h5" color={colors.primary[450]} sx={{ fontWeight: "bold" }}>
                                    {formatHours(moduleCurrentHours)}
                                </Typography>
                            </Box>
                            <Box sx={{
                                flex: 1,
                                p: 2,
                                bgcolor: colors.primary[600],
                                borderRadius: 1,
                                textAlign: "center",
                            }}>
                                <Typography variant="body1" color={colors.primary[450]}>
                                    Current adjustment
                                </Typography>
                                <Typography variant="h5" color={colors.primary[450]} sx={{ fontWeight: "bold" }}>
                                    {formatAdjustment(currentTotal)}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Delta input section */}
                        <Typography variant="body2" color={colors.primary[450]} sx={{ mt: 2 }}>
                            Enter an amount to add or subtract from the current total:
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 2, mt: 2 }}>
                            <ToggleButtonGroup
                                value={sign}
                                exclusive
                                onChange={(e, val) => { if (val !== null) setSign(val); }}
                                size="large"
                            >
                                <ToggleButton value="+" sx={{ width: 50, fontSize: "1.2rem", fontWeight: "bold" }}>+</ToggleButton>
                                <ToggleButton value="-" sx={{ width: 50, fontSize: "1.2rem", fontWeight: "bold" }}>−</ToggleButton>
                            </ToggleButtonGroup>
                            <TextField
                                type="number"
                                label="Hours"
                                variant="outlined"
                                value={deltaHours}
                                onChange={(e) => setDeltaHours(Math.max(0, parseInt(e.target.value) || 0))}
                                inputProps={{ min: 0 }}
                                sx={{ width: 120 }}
                            />
                            <TextField
                                type="number"
                                label="Minutes"
                                variant="outlined"
                                value={deltaMinutes}
                                onChange={(e) => setDeltaMinutes(Math.min(59, Math.max(0, parseInt(e.target.value) || 0)))}
                                inputProps={{ min: 0, max: 59 }}
                                sx={{ width: 120 }}
                            />
                        </Box>

                        {/* Live preview */}
                        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                            <Box sx={{
                                flex: 1,
                                p: 2,
                                bgcolor: isDeltaZero ? colors.primary[600] : colors.greenAccent[700],
                                borderRadius: 1,
                                textAlign: "center",
                            }}>
                                <Typography variant="body1" color={colors.primary[450]}>
                                    New module hours
                                </Typography>
                                <Typography variant="h5" color={colors.primary[450]} sx={{ fontWeight: "bold" }}>
                                    {isDeltaZero ? formatHours(moduleCurrentHours) : formatHours(moduleNewHours)}
                                </Typography>
                            </Box>
                            <Box sx={{
                                flex: 1,
                                p: 2,
                                bgcolor: isDeltaZero ? colors.primary[600] : colors.greenAccent[700],
                                borderRadius: 1,
                                textAlign: "center",
                            }}>
                                <Typography variant="body1" color={colors.primary[450]}>
                                    New adjustment
                                </Typography>
                                <Typography variant="h5" color={colors.primary[450]} sx={{ fontWeight: "bold" }}>
                                    {isDeltaZero ? formatAdjustment(currentTotal) : formatAdjustment(newTotal)}
                                </Typography>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 2,
                                mt: 2,
                            }}
                        >
                            <Button
                                variant="contained"
                                color="secondary"
                                sx={{ width: 200, mt: 2 }}
                                onClick={() => handleSave()}
                                disabled={isDeltaZero}
                            >
                                Save
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                sx={{ width: 200, mt: 2 }}
                                onClick={() => handleClose()}
                            >
                                Close
                            </Button>
                        </Box>
                    </Box>
                </Fade>
            </Modal>
        </>
    )
}

export default ModuleCurrentHoursAdjustmentButton
